import React, {useEffect, useRef, useState} from "react";
import {useFormContext, useWatch} from "react-hook-form";
import {css, cx} from "@panda/css";
import {AnimatePresence, motion} from "framer-motion";

import {Button} from "@pg-design/button-module";
import {calculateRemSize} from "@pg-design/helpers-css-strings";
import {MAX_VALUE_INPUT_LIMITER} from "@pg-design/inputs-module";
import {CheckboxControlled, MaxValueFieldControlled, SelectRangeFieldControlled, SwitcherControlled} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {Currency} from "@pg-rp/common-types";

import {getCurrency} from "../../utils/get_currency";
import {PREFERENCES_FORM_STEP} from "../constants/preferences/application_preferences_form";
import {FOR_YOU_FORM_LABELS} from "../constants/preferences/application_preferences_labels";
import {IPreferencesDefaultValues} from "../types/IPreferencesDefaultValues";
import {UserPreferencesRegionField} from "./preferences/UserPreferencesRegionField";
import {UserPreferencesRegionMap} from "./preferences/UserPreferencesRegionMap";

interface IProps {
    onSubmit: (values: IPreferencesDefaultValues, isExplicitSubmit: boolean) => void;
    header?: React.ReactNode;
    topFormContent?: React.ReactNode;
    currency: Currency;
    maxBudget: number;
    formWrapperClassName?: string;
    onFieldInteraction?: (section: string, value: string) => void;
    showMarketingConsents?: boolean;
    locationFieldTestId?: string;
    budgetInputTestId?: string;
    creditPlanSwitchTestId?: string;
    savePreferencesButtonTestId?: string;
    closeButtonTestId?: string;
    submitButtonText?: string;
    closeButtonText?: string;
    showCreditPlan?: boolean;
    actionsStickyToBottomOnMobile?: boolean;
    onCancel?: () => void;
}

export const PreferencesForm = (props: IProps) => {
    const {
        onSubmit,
        currency,
        header,
        topFormContent,
        formWrapperClassName,
        onFieldInteraction,
        showMarketingConsents,
        locationFieldTestId,
        budgetInputTestId,
        creditPlanSwitchTestId,
        savePreferencesButtonTestId,
        closeButtonTestId,
        submitButtonText = "Zapisz swoje preferencje",
        closeButtonText = "Zamknij",
        showCreditPlan = true,
        actionsStickyToBottomOnMobile,
        onCancel
    } = props;
    const [isLocationFocused, setIsLocationFocused] = useState(false);
    const {control, handleSubmit} = useFormContext<IPreferencesDefaultValues>();
    const submitIntentRef = useRef(true);

    const budgetValue = useWatch({control, name: "budget"});
    const roomsValue = useWatch({control, name: "rooms"});
    const creditPlanValue = useWatch({control, name: "creditPlan"});

    const onInputClick = (defaultAction: () => void) => {
        defaultAction();
        setIsLocationFocused(true);
    };

    const onActionButtonClick = () => {
        setIsLocationFocused(false);
    };

    const formatBudget = (value: number | undefined): string => {
        if (!value || value >= props.maxBudget) {
            return "bez limitu";
        }
        return `${value.toLocaleString("pl-PL")} zł`;
    };

    const formatRooms = (rooms: {lower: number | string; upper: number | string}): string => {
        const lower = typeof rooms.lower === "number" ? rooms.lower : "";
        const upper = typeof rooms.upper === "number" ? rooms.upper : "";

        if (lower && upper) {
            if (lower === upper) {
                return `${lower}`;
            }
            return `${lower}-${upper}`;
        }
        if (lower) {
            return `${lower}`;
        }
        if (upper) {
            return `${upper}`;
        }
        return "";
    };

    const handleRegionChange = (regionName: string) => {
        if (onFieldInteraction) {
            onFieldInteraction(FOR_YOU_FORM_LABELS.regionSearch, regionName);
        }
    };

    const budgetTracked = useRef(false);
    const roomsTracked = useRef(false);
    const creditPlanTracked = useRef(false);

    useEffect(() => {
        if (budgetTracked.current && onFieldInteraction && budgetValue !== undefined && budgetValue !== null && budgetValue < MAX_VALUE_INPUT_LIMITER) {
            onFieldInteraction(FOR_YOU_FORM_LABELS.budget, formatBudget(budgetValue));
        }
        budgetTracked.current = true;
    }, [budgetValue]);

    useEffect(() => {
        if (roomsTracked.current && onFieldInteraction && roomsValue) {
            const formattedValue = formatRooms(roomsValue);
            if (formattedValue) {
                onFieldInteraction(FOR_YOU_FORM_LABELS.rooms, formattedValue);
            }
        }
        roomsTracked.current = true;
    }, [roomsValue]);

    useEffect(() => {
        if (creditPlanTracked.current && onFieldInteraction && creditPlanValue !== undefined) {
            onFieldInteraction(FOR_YOU_FORM_LABELS.creditPlan, creditPlanValue ? "tak" : "nie");
        }
        creditPlanTracked.current = true;
    }, [creditPlanValue]);

    return (
        <div>
            {header ? (
                <motion.div
                    animate={{
                        opacity: isLocationFocused ? 0 : 1,
                        height: isLocationFocused ? 0 : "auto"
                    }}
                    transition={transition}
                    className={css({
                        p: 2,
                        md: {p: 0}
                    })}
                >
                    {header}
                </motion.div>
            ) : null}
            <form onSubmit={handleSubmit((values) => onSubmit(values, submitIntentRef.current))} className={formWrapperClassName}>
                <div className={cx(formStyle, actionsStickyToBottomOnMobile && mobileStickyActionsFormStyle)}>
                    {topFormContent ? (
                        <motion.div
                            animate={{
                                opacity: isLocationFocused ? 0 : 1,
                                height: isLocationFocused ? 0 : "auto"
                            }}
                            transition={transition}
                        >
                            {topFormContent}
                        </motion.div>
                    ) : null}
                    <motion.div
                        animate={{
                            marginTop: isLocationFocused ? 0 : calculateRemSize(4)
                        }}
                        transition={transition}
                    >
                        <div {...(locationFieldTestId && {"data-testid": locationFieldTestId})}>
                            <UserPreferencesRegionField
                                name="regionSelector"
                                label={isLocationFocused ? undefined : FOR_YOU_FORM_LABELS.regionSearch}
                                onInputClick={onInputClick}
                                onRegionSelect={handleRegionChange}
                            />
                        </div>
                    </motion.div>
                    <motion.div
                        animate={{
                            opacity: isLocationFocused ? 0 : 1,
                            height: isLocationFocused ? 0 : "auto"
                        }}
                        transition={transition}
                    >
                        <div className={preferencesWrapper}>
                            <MaxValueFieldControlled
                                name="budget"
                                min={0}
                                max={props.maxBudget}
                                step={PREFERENCES_FORM_STEP}
                                rightElement={getCurrency({currency})}
                                labelContent={FOR_YOU_FORM_LABELS.budget}
                                className={sliderStyle}
                                inputClassName={inputStyle}
                                maxDisplayLabel="bez limitu"
                                centerValue={500_000}
                                dataTestId={budgetInputTestId}
                            />
                            {showCreditPlan ? (
                                <div className={switcherMobile}>
                                    <SwitcherControlled
                                        labelContent={FOR_YOU_FORM_LABELS.creditPlan}
                                        name="creditPlan"
                                        wrapperClassName={switchStyle}
                                        dataTestId={creditPlanSwitchTestId}
                                    />
                                </div>
                            ) : null}
                            <div className={roomsSelectWrapper}>
                                <Text variant="body_copy_2">{FOR_YOU_FORM_LABELS.rooms}</Text>
                                <SelectRangeFieldControlled
                                    name="rooms"
                                    options={[
                                        {id: "1", label: "1", value: 1},
                                        {id: "2", label: "2", value: 2},
                                        {id: "3", label: "3", value: 3},
                                        {id: "4", label: "4", value: 4},
                                        {id: "5", label: "5+", value: 5}
                                    ]}
                                    itemClassName={rangeSelectItem}
                                />
                            </div>
                        </div>
                        {showCreditPlan ? (
                            <div className={switcherDesktop}>
                                <SwitcherControlled
                                    labelContent={FOR_YOU_FORM_LABELS.creditPlan}
                                    name="creditPlan"
                                    wrapperClassName={switchStyle}
                                    dataTestId={creditPlanSwitchTestId}
                                />
                            </div>
                        ) : null}
                        {showMarketingConsents && (
                            <div className={consentsWrapper}>
                                <Text variant="info_txt_3" as="div">
                                    Wyrażam zgodę na wykorzystanie przez Property Group sp. z o.o. z siedzibą w Warszawie powyżej wskazanych danych kontaktowych
                                    (email, telefon) do przesyłania informacji handlowych drogą elektroniczną oraz do celów marketingu bezpośredniego, w tym w
                                    odniesieniu do nr telefonu – z użyciem automatycznych systemów wywołujących:
                                </Text>
                                <div className={consentsCheckboxesWrapper}>
                                    <CheckboxControlled
                                        name="marketing_email"
                                        id="preferences_marketing_email"
                                        labelContent={
                                            <Text as="span" variant="info_txt_1">
                                                o adres email
                                            </Text>
                                        }
                                        checkedMarkColor="#02D054"
                                    />
                                    <CheckboxControlled
                                        name="marketing_sms"
                                        id="preferences_marketing_sms"
                                        labelContent={
                                            <Text as="span" variant="info_txt_1">
                                                o nr telefonu
                                            </Text>
                                        }
                                        checkedMarkColor="#02D054"
                                    />
                                </div>
                            </div>
                        )}
                    </motion.div>

                    <motion.div
                        animate={{
                            opacity: isLocationFocused ? 0 : 1,
                            height: isLocationFocused ? 0 : "auto"
                        }}
                        transition={transition}
                        className={cx(isLocationFocused && hiddenActionsWrapperStyle)}
                    >
                        <div className={cx(stickyActionsBar, actionsStickyToBottomOnMobile && mobileStickyActionsBar)}>
                            <Button
                                variant="filled_primary"
                                size="medium"
                                type="submit"
                                className={btnStyle}
                                onClick={() => (submitIntentRef.current = true)}
                                {...(savePreferencesButtonTestId && {dataTestId: savePreferencesButtonTestId})}
                            >
                                {submitButtonText}
                            </Button>

                            <Button
                                variant="none_secondary"
                                size="medium"
                                type={onCancel ? "button" : "submit"}
                                className={btnStyle}
                                onClick={() => {
                                    submitIntentRef.current = false;
                                    onCancel?.();
                                }}
                                {...(closeButtonTestId && {dataTestId: closeButtonTestId})}
                            >
                                {closeButtonText}
                            </Button>
                        </div>
                    </motion.div>
                    <AnimatePresence>
                        {isLocationFocused && (
                            <motion.div
                                initial={{opacity: 0, height: 0}}
                                animate={{opacity: 1, height: "auto"}}
                                exit={{opacity: 0, height: 0}}
                                transition={transition}
                            >
                                <div className={mapHolder}>
                                    <UserPreferencesRegionMap onActionButtonClick={onActionButtonClick} />
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </form>
        </div>
    );
};

const transition = {
    type: "spring",
    stiffness: 300,
    damping: 30
};

const formStyle = css({
    pb: 2,
    borderRadius: 1,

    md: {
        p: 0
    }
});

const mobileStickyActionsFormStyle = css({
    pb: 2,

    sm: {
        pb: 0
    }
});

const hiddenActionsWrapperStyle = css({
    pointerEvents: "none"
});

const preferencesWrapper = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "column",
    gap: 3,
    mt: 2,

    md: {
        alignItems: "start"
    }
});

const switcher = css.raw({
    p: 1,
    borderRadius: 1,
    background: "var(--colors-gray-200)"
});

const switcherDesktop = css({
    ...switcher,
    display: "none",
    mt: 2,

    md: {
        display: "block"
    }
});

const switcherMobile = css({
    ...switcher,
    width: "100%",

    md: {
        display: "none"
    }
});

const stickyActionsBar = css({
    position: "sticky",
    pt: 3,
    bottom: 0,
    backgroundColor: "var(--colors-white)",
    zIndex: 10
});

const mobileStickyActionsBar = css({
    position: "sticky",
    pt: 2,
    backgroundColor: "var(--colors-white)",
    zIndex: 20,

    sm: {
        pt: 3
    }
});

const btnStyle = css({
    width: "100%"
});

const roomsSelectWrapper = css({
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
    width: "100%"
});

const sliderStyle = css({
    pl: 1,

    md: {
        pl: 0
    }
});

const inputStyle = css({
    width: "75px"
});

const rangeSelectItem = css({
    height: "48px",
    borderColor: "var(--colors-gray-600) !important"
});

const switchStyle = css({
    display: "flex",
    justifyContent: "flex-end",
    flexDirection: "row-reverse",
    gap: 1
});

const mapHolder = css({
    minHeight: "460px"
});

const consentsWrapper = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    mt: 2
});

const consentsCheckboxesWrapper = css({
    display: "flex",
    flexDirection: "row",
    gap: 3
});
import React, {useEffect, useRef, useState} from "react";
import {useFormContext, useWatch} from "react-hook-form";
import {css, cx} from "@panda/css";
import {AnimatePresence, motion} from "framer-motion";

import {Button} from "@pg-design/button-module";
import {calculateRemSize} from "@pg-design/helpers-css-strings";
import {MAX_VALUE_INPUT_LIMITER} from "@pg-design/inputs-module";
import {CheckboxControlled, MaxValueFieldControlled, SelectRangeFieldControlled, SwitcherControlled} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {Currency} from "@pg-rp/common-types";

import {getCurrency} from "../../utils/get_currency";
import {PREFERENCES_FORM_STEP} from "../constants/preferences/application_preferences_form";
import {FOR_YOU_FORM_LABELS} from "../constants/preferences/application_preferences_labels";
import {IPreferencesDefaultValues} from "../types/IPreferencesDefaultValues";
import {UserPreferencesRegionField} from "./preferences/UserPreferencesRegionField";
import {UserPreferencesRegionMap} from "./preferences/UserPreferencesRegionMap";

interface IProps {
    onSubmit: (values: IPreferencesDefaultValues, isExplicitSubmit: boolean) => void;
    header?: React.ReactNode;
    topFormContent?: React.ReactNode;
    currency: Currency;
    maxBudget: number;
    formWrapperClassName?: string;
    onFieldInteraction?: (section: string, value: string) => void;
    showMarketingConsents?: boolean;
    locationFieldTestId?: string;
    budgetInputTestId?: string;
    creditPlanSwitchTestId?: string;
    savePreferencesButtonTestId?: string;
    closeButtonTestId?: string;
    submitButtonText?: string;
    closeButtonText?: string;
    showCreditPlan?: boolean;
    actionsStickyToBottomOnMobile?: boolean;
    onCancel?: () => void;
}

export const PreferencesForm = (props: IProps) => {
    const {
        onSubmit,
        currency,
        header,
        topFormContent,
        formWrapperClassName,
        onFieldInteraction,
        showMarketingConsents,
        locationFieldTestId,
        budgetInputTestId,
        creditPlanSwitchTestId,
        savePreferencesButtonTestId,
        closeButtonTestId,
        submitButtonText = "Zapisz swoje preferencje",
        closeButtonText = "Zamknij",
        showCreditPlan = true,
        actionsStickyToBottomOnMobile,
        onCancel
    } = props;
    const [isLocationFocused, setIsLocationFocused] = useState(false);
    const {control, handleSubmit} = useFormContext<IPreferencesDefaultValues>();
    const submitIntentRef = useRef(true);

    const budgetValue = useWatch({control, name: "budget"});
    const roomsValue = useWatch({control, name: "rooms"});
    const creditPlanValue = useWatch({control, name: "creditPlan"});

    const onInputClick = (defaultAction: () => void) => {
        defaultAction();
        setIsLocationFocused(true);
    };

    const onActionButtonClick = () => {
        setIsLocationFocused(false);
    };

    const formatBudget = (value: number | undefined): string => {
        if (!value || value >= props.maxBudget) {
            return "bez limitu";
        }
        return `${value.toLocaleString("pl-PL")} zł`;
    };

    const formatRooms = (rooms: {lower: number | string; upper: number | string}): string => {
        const lower = typeof rooms.lower === "number" ? rooms.lower : "";
        const upper = typeof rooms.upper === "number" ? rooms.upper : "";

        if (lower && upper) {
            if (lower === upper) {
                return `${lower}`;
            }
            return `${lower}-${upper}`;
        }
        if (lower) {
            return `${lower}`;
        }
        if (upper) {
            return `${upper}`;
        }
        return "";
    };

    const handleRegionChange = (regionName: string) => {
        if (onFieldInteraction) {
            onFieldInteraction(FOR_YOU_FORM_LABELS.regionSearch, regionName);
        }
    };

    const budgetTracked = useRef(false);
    const roomsTracked = useRef(false);
    const creditPlanTracked = useRef(false);

    useEffect(() => {
        if (budgetTracked.current && onFieldInteraction && budgetValue !== undefined && budgetValue !== null && budgetValue < MAX_VALUE_INPUT_LIMITER) {
            onFieldInteraction(FOR_YOU_FORM_LABELS.budget, formatBudget(budgetValue));
        }
        budgetTracked.current = true;
    }, [budgetValue]);

    useEffect(() => {
        if (roomsTracked.current && onFieldInteraction && roomsValue) {
            const formattedValue = formatRooms(roomsValue);
            if (formattedValue) {
                onFieldInteraction(FOR_YOU_FORM_LABELS.rooms, formattedValue);
            }
        }
        roomsTracked.current = true;
    }, [roomsValue]);

    useEffect(() => {
        if (creditPlanTracked.current && onFieldInteraction && creditPlanValue !== undefined) {
            onFieldInteraction(FOR_YOU_FORM_LABELS.creditPlan, creditPlanValue ? "tak" : "nie");
        }
        creditPlanTracked.current = true;
    }, [creditPlanValue]);

    return (
        <div>
            {header ? (
                <motion.div
                    animate={{
                        opacity: isLocationFocused ? 0 : 1,
                        height: isLocationFocused ? 0 : "auto"
                    }}
                    transition={transition}
                    className={css({
                        p: 2,
                        md: {p: 0}
                    })}
                >
                    {header}
                </motion.div>
            ) : null}
            <form onSubmit={handleSubmit((values) => onSubmit(values, submitIntentRef.current))} className={formWrapperClassName}>
                <div className={cx(formStyle, actionsStickyToBottomOnMobile && mobileStickyActionsFormStyle)}>
                    {topFormContent ? (
                        <motion.div
                            animate={{
                                opacity: isLocationFocused ? 0 : 1,
                                height: isLocationFocused ? 0 : "auto"
                            }}
                            transition={transition}
                        >
                            {topFormContent}
                        </motion.div>
                    ) : null}
                    <motion.div
                        animate={{
                            marginTop: isLocationFocused ? 0 : calculateRemSize(4)
                        }}
                        transition={transition}
                    >
                        <div {...(locationFieldTestId && {"data-testid": locationFieldTestId})}>
                            <UserPreferencesRegionField
                                name="regionSelector"
                                label={isLocationFocused ? undefined : FOR_YOU_FORM_LABELS.regionSearch}
                                onInputClick={onInputClick}
                                onRegionSelect={handleRegionChange}
                            />
                        </div>
                    </motion.div>
                    <motion.div
                        animate={{
                            opacity: isLocationFocused ? 0 : 1,
                            height: isLocationFocused ? 0 : "auto"
                        }}
                        transition={transition}
                    >
                        <div className={preferencesWrapper}>
                            <MaxValueFieldControlled
                                name="budget"
                                min={0}
                                max={props.maxBudget}
                                step={PREFERENCES_FORM_STEP}
                                rightElement={getCurrency({currency})}
                                labelContent={FOR_YOU_FORM_LABELS.budget}
                                className={sliderStyle}
                                inputClassName={inputStyle}
                                maxDisplayLabel="bez limitu"
                                centerValue={500_000}
                                dataTestId={budgetInputTestId}
                            />
                            {showCreditPlan ? (
                                <div className={switcherMobile}>
                                    <SwitcherControlled
                                        labelContent={FOR_YOU_FORM_LABELS.creditPlan}
                                        name="creditPlan"
                                        wrapperClassName={switchStyle}
                                        dataTestId={creditPlanSwitchTestId}
                                    />
                                </div>
                            ) : null}
                            <div className={roomsSelectWrapper}>
                                <Text variant="body_copy_2">{FOR_YOU_FORM_LABELS.rooms}</Text>
                                <SelectRangeFieldControlled
                                    name="rooms"
                                    options={[
                                        {id: "1", label: "1", value: 1},
                                        {id: "2", label: "2", value: 2},
                                        {id: "3", label: "3", value: 3},
                                        {id: "4", label: "4", value: 4},
                                        {id: "5", label: "5+", value: 5}
                                    ]}
                                    itemClassName={rangeSelectItem}
                                />
                            </div>
                        </div>
                        {showCreditPlan ? (
                            <div className={switcherDesktop}>
                                <SwitcherControlled
                                    labelContent={FOR_YOU_FORM_LABELS.creditPlan}
                                    name="creditPlan"
                                    wrapperClassName={switchStyle}
                                    dataTestId={creditPlanSwitchTestId}
                                />
                            </div>
                        ) : null}
                        {showMarketingConsents && (
                            <div className={consentsWrapper}>
                                <Text variant="info_txt_3" as="div">
                                    Wyrażam zgodę na wykorzystanie przez Property Group sp. z o.o. z siedzibą w Warszawie powyżej wskazanych danych kontaktowych
                                    (email, telefon) do przesyłania informacji handlowych drogą elektroniczną oraz do celów marketingu bezpośredniego, w tym w
                                    odniesieniu do nr telefonu – z użyciem automatycznych systemów wywołujących:
                                </Text>
                                <div className={consentsCheckboxesWrapper}>
                                    <CheckboxControlled
                                        name="marketing_email"
                                        id="preferences_marketing_email"
                                        labelContent={
                                            <Text as="span" variant="info_txt_1">
                                                o adres email
                                            </Text>
                                        }
                                        checkedMarkColor="#02D054"
                                    />
                                    <CheckboxControlled
                                        name="marketing_sms"
                                        id="preferences_marketing_sms"
                                        labelContent={
                                            <Text as="span" variant="info_txt_1">
                                                o nr telefonu
                                            </Text>
                                        }
                                        checkedMarkColor="#02D054"
                                    />
                                </div>
                            </div>
                        )}
                    </motion.div>

                    <motion.div
                        animate={{
                            opacity: isLocationFocused ? 0 : 1,
                            height: isLocationFocused ? 0 : "auto"
                        }}
                        transition={transition}
                        className={cx(isLocationFocused && hiddenActionsWrapperStyle)}
                    >
                        <div className={cx(stickyActionsBar, actionsStickyToBottomOnMobile && mobileStickyActionsBar)}>
                            <Button
                                variant="filled_primary"
                                size="medium"
                                type="submit"
                                className={btnStyle}
                                onClick={() => (submitIntentRef.current = true)}
                                {...(savePreferencesButtonTestId && {dataTestId: savePreferencesButtonTestId})}
                            >
                                {submitButtonText}
                            </Button>

                            <Button
                                variant="none_secondary"
                                size="medium"
                                type={onCancel ? "button" : "submit"}
                                className={btnStyle}
                                onClick={() => {
                                    submitIntentRef.current = false;
                                    onCancel?.();
                                }}
                                {...(closeButtonTestId && {dataTestId: closeButtonTestId})}
                            >
                                {closeButtonText}
                            </Button>
                        </div>
                    </motion.div>
                    <AnimatePresence>
                        {isLocationFocused && (
                            <motion.div
                                initial={{opacity: 0, height: 0}}
                                animate={{opacity: 1, height: "auto"}}
                                exit={{opacity: 0, height: 0}}
                                transition={transition}
                            >
                                <div className={mapHolder}>
                                    <UserPreferencesRegionMap onActionButtonClick={onActionButtonClick} />
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </form>
        </div>
    );
};

const transition = {
    type: "spring",
    stiffness: 300,
    damping: 30
};

const formStyle = css({
    pb: 2,
    borderRadius: 1,

    md: {
        p: 0
    }
});

const mobileStickyActionsFormStyle = css({
    pb: 2,

    sm: {
        pb: 0
    }
});

const hiddenActionsWrapperStyle = css({
    pointerEvents: "none"
});

const preferencesWrapper = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "column",
    gap: 3,
    mt: 2,

    md: {
        alignItems: "start"
    }
});

const switcher = css.raw({
    p: 1,
    borderRadius: 1,
    background: "var(--colors-gray-200)"
});

const switcherDesktop = css({
    ...switcher,
    display: "none",
    mt: 2,

    md: {
        display: "block"
    }
});

const switcherMobile = css({
    ...switcher,
    width: "100%",

    md: {
        display: "none"
    }
});

const stickyActionsBar = css({
    position: "sticky",
    pt: 3,
    bottom: 0,
    backgroundColor: "var(--colors-white)",
    zIndex: 10
});

const mobileStickyActionsBar = css({
    position: "sticky",
    pt: 2,
    backgroundColor: "var(--colors-white)",
    zIndex: 20,

    sm: {
        pt: 3
    }
});

const btnStyle = css({
    width: "100%"
});

const roomsSelectWrapper = css({
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
    width: "100%"
});

const sliderStyle = css({
    pl: 1,

    md: {
        pl: 0
    }
});

const inputStyle = css({
    width: "75px"
});

const rangeSelectItem = css({
    height: "48px",
    borderColor: "var(--colors-gray-600) !important"
});

const switchStyle = css({
    display: "flex",
    justifyContent: "flex-end",
    flexDirection: "row-reverse",
    gap: 1
});

const mapHolder = css({
    minHeight: "460px"
});

const consentsWrapper = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    mt: 2
});

const consentsCheckboxesWrapper = css({
    display: "flex",
    flexDirection: "row",
    gap: 3
});
